import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../i18n';
import { supabase } from '../lib/supabase';
import { loadAndCacheUserFonts, injectCachedFonts } from '../utils/fontCache';
import AppHeader from '../components/AppHeader';
import AlertDialog from '../components/AlertDialog';
import ConfirmDialog from '../components/ConfirmDialog';
import { useAlert } from '../hooks/useAlert';
import { getCacheData, setCacheData } from '../utils/dataCache';

interface CustomFont {
  id: string;
  font_name: string;
  font_file_url: string;
  font_family_css: string;
  created_at: string;
}

const CustomFonts = () => {
  const [fonts, setFonts] = useState<CustomFont[]>([]);
  const [loading, setLoading] = useState(false); // ✨ بدون loading في البداية
  const [uploading, setUploading] = useState(false);
  const [fontName, setFontName] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t, dir } = useLanguage();
  const { showAlert, AlertComponent } = useAlert();
  
  // Dialog states
  const [showInsufficientCreditsDialog, setShowInsufficientCreditsDialog] = useState(false);
  const [insufficientCreditsMessage, setInsufficientCreditsMessage] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [fontToDelete, setFontToDelete] = useState<{ id: string; url: string } | null>(null);

  useEffect(() => {
    if (!user) {
      navigate('/signin');
      return;
    }
    fetchFonts();
  }, [user, navigate]);

  const fetchFonts = async (showLoadingIndicator: boolean = false) => {
    try {
      // 📦 محاولة تحميل البيانات من الـ cache أولاً
      const cacheKey = `custom_fonts_${user?.id}`;
      const cachedFonts = getCacheData<CustomFont[]>(cacheKey);
      
      if (cachedFonts && cachedFonts.length > 0) {
        console.log('✅ Loaded fonts from cache');
        setFonts(cachedFonts);
        // تحديث البيانات في الخلفية دون إظهار loading
        showLoadingIndicator = false;
      } else {
        // 📥 أول مرة، إظهار loading
        console.log('📥 Loading fonts for the first time...');
        showLoadingIndicator = true;
      }
      
      if (showLoadingIndicator) {
        setLoading(true);
      }

      const { data, error } = await supabase
        .from('custom_fonts')
        .select('*')
        .eq('user_id', user!.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      const freshFonts = data || [];
      setFonts(freshFonts);
      
      // 💾 حفظ في الـ cache
      setCacheData(cacheKey, freshFonts);
      
    } catch (err: any) {
      console.error('Error loading fonts:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadFont = async () => {
    if (!fontName.trim()) {
      showAlert({
        title: t('pages.customFonts.errors.title'),
        message: t('pages.customFonts.errors.nameRequired'),
        type: 'warning'
      });
      return;
    }

    if (!selectedFile) {
      showAlert({
        title: t('pages.customFonts.errors.title'),
        message: t('pages.customFonts.errors.fileRequired'),
        type: 'warning'
      });
      return;
    }

    setUploading(true);
    try {
      // التحقق من توفر الكريدت (10 كريدت مطلوبة) من النظام الجديد
      const { data: creditsValue } = await supabase.rpc('get_user_available_credits', { 
        p_user_id: user!.id 
      });

      const remaining = Number(creditsValue) || 0;
      if (remaining < 10) {
        showAlert({
          title: t('pages.customFonts.errors.insufficientCredits'),
          message: t('pages.customFonts.errors.insufficientCreditsMessage', { credits: remaining }),
          type: 'error'
        });
        setUploading(false);
        return;
      }

      // Upload file to Supabase Storage
      const fileExt = selectedFile.name.split('.').pop();
      const fileName = `${user!.id}/${Date.now()}.${fileExt}`;
      
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('fonts')
        .upload(fileName, selectedFile, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        console.error('Upload error:', uploadError);
        throw new Error(t('pages.customFonts.errors.uploadFailed'));
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('fonts')
        .getPublicUrl(fileName);

      // Save to database
      const { data: fontData, error: dbError } = await supabase
        .from('custom_fonts')
        .insert({
          user_id: user!.id,
          font_name: fontName,
          font_file_url: urlData.publicUrl,
          font_family_css: `'${fontName}', sans-serif`,
        })
        .select()
        .single();

      if (dbError) throw dbError;

      // خصم 10 كريدت بعد نجاح الإدراج، مع تراجع عند الفشل
      try {
        const { data: creditResult, error: creditError } = await supabase.rpc('use_credits_fifo', {
          p_user_id: user!.id,
          p_amount: 10,
          p_reason: `${t('pages.customFonts.uploadReason')}: ${fontName}`
        });

        if (creditError || (creditResult && creditResult.success === false)) {
          // Rollback DB row and storage file
          await supabase.from('custom_fonts').delete().eq('id', fontData.id);
          await supabase.storage.from('fonts').remove([fileName]);
          throw new Error((creditError && creditError.message) || creditResult?.error || t('pages.customFonts.errors.deductFailed'));
        }
      } catch (deductErr: any) {
        throw deductErr;
      }

      showAlert({
        title: t('pages.customFonts.success.title'),
        message: t('pages.customFonts.success.message'),
        type: 'success'
      });
      setFontName('');
      setSelectedFile(null);
      
      // تحديث البيانات في الخلفية بدون loading
      await fetchFonts(false);
      
      // Update font cache
      const { data: updatedFonts } = await supabase
        .from('custom_fonts')
        .select('id, font_name, font_file_url, font_family_css')
        .eq('user_id', user!.id);
      
      if (updatedFonts) {
        const cachedFonts = await loadAndCacheUserFonts(user!.id, updatedFonts);
        injectCachedFonts(cachedFonts);
      }
    } catch (err: any) {
      showAlert({
        title: t('pages.customFonts.errors.uploadFailedTitle'),
        message: err.message,
        type: 'error'
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteFont = (fontId: string, fileUrl: string) => {
    setFontToDelete({ id: fontId, url: fileUrl });
    setShowDeleteConfirm(true);
  };

  const confirmDeleteFont = async () => {
    if (!fontToDelete) return;

    try {
      // Extract file path from URL
      const urlParts = fontToDelete.url.split('/fonts/');
      if (urlParts.length > 1) {
        const filePath = urlParts[1];
        await supabase.storage.from('fonts').remove([filePath]);
      }

      // Delete from database
      const { error } = await supabase
        .from('custom_fonts')
        .delete()
        .eq('id', fontToDelete.id);

      if (error) throw error;

      const updatedFonts = fonts.filter(f => f.id !== fontToDelete.id);
      setFonts(updatedFonts);
      
      // 💾 تحديث الـ cache
      const cacheKey = `custom_fonts_${user?.id}`;
      setCacheData(cacheKey, updatedFonts);
      
      // Update font cache
      const { data: freshFonts } = await supabase
        .from('custom_fonts')
        .select('id, font_name, font_file_url, font_family_css')
        .eq('user_id', user!.id);
      
      if (freshFonts) {
        const cachedFonts = await loadAndCacheUserFonts(user!.id, freshFonts);
        injectCachedFonts(cachedFonts);
      }
    } catch (err: any) {
      console.error('Error deleting font:', err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white text-xl">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <>
      <AlertComponent />
      <div className="min-h-screen bg-black" dir={dir} style={{ overflow: 'auto', height: '100vh' }}>
      <AppHeader />
      
      {/* Sub-header */}
      <div className="bg-zinc-900/50 border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <h2 className="text-xl font-bold text-white">{t('pages.customFonts.title')}</h2>
          <p className="text-sm text-gray-400 mt-1">{t('pages.customFonts.subtitle')}</p>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Upload Section */}
        <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-white">{t('pages.customFonts.uploadNew')}</h2>
            <div className="bg-purple-500/20 border border-purple-500/50 rounded-lg px-3 py-1">
              <span className="text-purple-300 text-sm font-semibold">{t('pages.customFonts.cost')}</span>
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t('pages.customFonts.fontName')}
              </label>
              <input
                type="text"
                value={fontName}
                onChange={(e) => setFontName(e.target.value)}
                className="w-full bg-zinc-800 border border-zinc-700 text-white px-4 py-3 rounded-lg focus:border-blue-500 focus:outline-none"
                placeholder={t('pages.customFonts.fontNamePlaceholder')}
                disabled={uploading}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t('pages.customFonts.fontFile')}
              </label>
              <input
                type="file"
                accept=".ttf,.otf,.woff,.woff2"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) setSelectedFile(file);
                }}
                className="w-full bg-zinc-800 border border-zinc-700 text-white px-4 py-3 rounded-lg focus:border-blue-500 focus:outline-none file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-600 file:text-white hover:file:bg-blue-700"
                disabled={uploading}
              />
              {selectedFile && (
                <p className="text-sm text-green-400 mt-2">{t('pages.customFonts.fileSelected', { fileName: selectedFile.name })}</p>
              )}
            </div>
            
            {/* Upload Button */}
            <button
              onClick={handleUploadFont}
              disabled={uploading || !fontName || !selectedFile}
              className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {uploading ? (
                <>
                  <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t('pages.customFonts.uploading')}
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  {t('pages.customFonts.uploadButton')}
                </>
              )}
            </button>
          </div>
        </div>

        {/* Fonts List */}
        <div>
          <h2 className="text-xl font-bold text-white mb-4">{t('pages.customFonts.myFonts', { count: fonts.length })}</h2>
          {fonts.length === 0 ? (
            <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-12 text-center">
              <svg className="w-16 h-16 mx-auto text-gray-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              <p className="text-gray-400">{t('pages.customFonts.noFonts')}</p>
              <p className="text-gray-500 text-sm mt-2">{t('pages.customFonts.uploadFirst')}</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {fonts.map((font) => (
                <div
                  key={font.id}
                  className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 hover:border-blue-500 transition-colors"
                >
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-lg font-semibold text-white">{font.font_name}</h3>
                    <button
                      onClick={() => handleDeleteFont(font.id, font.font_file_url)}
                      className="text-red-400 hover:text-red-300 transition-colors"
                      title={t('pages.customFonts.delete')}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                  <div className="text-xs text-gray-500">
                    {new Date(font.created_at).toLocaleDateString('en-US')}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Insufficient Credits Dialog */}
      <AlertDialog
        isOpen={showInsufficientCreditsDialog}
        onClose={() => setShowInsufficientCreditsDialog(false)}
        title={t('pages.customFonts.insufficientCredits')}
        message={insufficientCreditsMessage}
        type="error"
        icon={
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        }
      />

      {/* Delete Font Confirmation */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setFontToDelete(null);
        }}
        onConfirm={confirmDeleteFont}
        title={t('pages.customFonts.deleteConfirm.title')}
        message={t('pages.customFonts.deleteConfirm.message')}
        confirmText={t('pages.customFonts.deleteConfirm.confirm')}
        cancelText={t('common.cancel')}
        type="danger"
        icon={
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </div>
        }
      />
    </div>
    </>
  );
};

export default CustomFonts;

